import type { I18nKey } from '@brao/shared';
import type { ApprovalDecision, ApprovalKind, ArtifactStatus, ExternalActionStatus, RunStatus } from '../api/runs';

/**
 * Labels and formatting shared by the Runs list, the run page and the task page.
 *
 * Enum → key maps instead of interpolated keys: the i18n guard only sees plain string literals in a
 * `t(...)` call, so a key built with `${}` would never be checked (`scripts/guards/i18n-keys.mjs`).
 */
export const RUN_STATUS_KEY: Record<RunStatus, I18nKey> = {
  queued: 'runs.status.queued',
  running: 'runs.status.running',
  waiting_approval: 'runs.status.waitingApproval',
  waiting_review: 'runs.status.waitingReview',
  retrying: 'runs.status.retrying',
  partially_completed: 'runs.status.partiallyCompleted',
  completed: 'runs.status.completed',
  failed: 'runs.status.failed',
  cancelled: 'runs.status.cancelled',
  expired: 'runs.status.expired',
};

export const ARTIFACT_STATUS_KEY: Record<ArtifactStatus, I18nKey> = {
  draft: 'runs.artifactStatus.draft',
  reviewed: 'runs.artifactStatus.reviewed',
  approved: 'runs.artifactStatus.approved',
  superseded: 'runs.artifactStatus.superseded',
};

export const ACTION_STATUS_KEY: Record<ExternalActionStatus, I18nKey> = {
  planned: 'runs.actionStatus.planned',
  executing: 'runs.actionStatus.executing',
  done: 'runs.actionStatus.done',
  failed: 'runs.actionStatus.failed',
  rolled_back: 'runs.actionStatus.rolledBack',
};

export const APPROVAL_KIND_KEY: Record<ApprovalKind, I18nKey> = {
  plan: 'runs.kind.plan',
  step: 'runs.kind.step',
  external_action: 'runs.kind.externalAction',
  new_agent: 'runs.kind.newAgent',
  knowledge: 'runs.kind.knowledge',
};

export const APPROVAL_DECISION_KEY: Record<ApprovalDecision, I18nKey> = {
  approved: 'runs.decision.approved',
  rework: 'runs.decision.rework',
  rejected: 'runs.decision.rejected',
};

/** Machine-readable API errors the runs endpoints answer with, in Ukrainian. */
export const RUN_ERROR_KEY: Record<string, I18nKey> = {
  run_not_found: 'runs.error.notFound',
  execution_not_found: 'runs.error.notFound',
  task_not_found: 'runs.error.taskNotFound',
  instance_not_found: 'runs.error.instanceNotActive',
  invalid_execution_status: 'runs.error.invalidStatus',
  budget_hard_limit: 'runs.error.budgetHardLimit',
  instance_not_active: 'runs.error.instanceNotActive',
};

/**
 * Status → badge class, per the block brief: green for a run that ended well, amber while it is
 * waiting on someone, red for the four ways a run can end without a result. `queued` / `running`
 * stay neutral: nothing has gone right or wrong yet.
 */
export function runStatusClass(status: RunStatus): string {
  if (status === 'completed' || status === 'partially_completed') return 'badge ok';
  if (status === 'waiting_approval' || status === 'waiting_review' || status === 'retrying') return 'badge warn';
  if (status === 'failed' || status === 'expired' || status === 'cancelled') return 'badge danger';
  return 'badge';
}

export function actionStatusClass(status: ExternalActionStatus): string {
  if (status === 'done') return 'badge ok';
  if (status === 'failed') return 'badge danger';
  if (status === 'executing' || status === 'planned') return 'badge warn';
  return 'badge';
}

export function decisionClass(decision: ApprovalDecision | null): string {
  if (decision === 'approved') return 'badge ok';
  if (decision === 'rejected') return 'badge danger';
  return 'badge warn';
}

/** "0:38", "1:04:12"; an em dash while the run has not started. Seconds only, never rounded up to a minute. */
export function fmtDuration(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec) || sec < 0) return '—';
  const s = Math.floor(sec);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${two(minutes)}:${two(seconds)}` : `${minutes}:${two(seconds)}`;
}

/**
 * Cost as the six-decimal string the api sends, trimmed to something readable but never rounded to
 * zero: four decimals normally ("0.0312"), all six when the run cost less than a hundredth of a
 * cent ("0.000045"). The currency is a separate word, because runs and revenue can be priced in
 * different ones.
 */
export function fmtCost(cost: string): string {
  const n = Number(cost);
  if (!Number.isFinite(n)) return cost;
  if (n === 0) return '0.0000';
  if (Math.abs(n) < 0.0001) return cost.replace(/0+$/, '').replace(/\.$/, '');
  return n.toFixed(4);
}

/** Anything the engine stored as JSON, pretty-printed for a `<pre>`; a plain string is shown as it is. */
export function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** The two blocks `buildContext` renders into `ExecutionProvenance.contextSnapshot`. */
export function contextOf(snapshot: unknown): { system: string; user: string } | null {
  if (typeof snapshot !== 'object' || snapshot === null || Array.isArray(snapshot)) return null;
  const s = snapshot as { system?: unknown; user?: unknown };
  if (typeof s.system !== 'string' && typeof s.user !== 'string') return null;
  return { system: typeof s.system === 'string' ? s.system : '', user: typeof s.user === 'string' ? s.user : '' };
}
